import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  CommunicationIntegration,
  TwilioIntegrationConfig,
} from "@/types/communication-integration";
import {
  createTwilioIntegrationSchema,
  TwilioIntegrationFormData,
} from "../utils/integration-schemas";
import { buildTwilioIntegrationPayload } from "../utils/payload-builders";
import { useUpsertTwilioIntegration } from "../hooks/communication-integrations.hooks";
import { SecretField } from "./SecretField";

interface TwilioIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  existing: CommunicationIntegration | null;
}

export const TwilioIntegrationDialog: React.FC<
  TwilioIntegrationDialogProps
> = ({ open, onOpenChange, hotelId, existing }) => {
  const { t } = useTranslation();
  const twilioConfig =
    existing?.config.kind === "TWILIO" ? existing.config : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existing
              ? t("communicationChannels.twilio.editTitle")
              : t("communicationChannels.twilio.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("communicationChannels.twilio.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <TwilioIntegrationForm
            hotelId={hotelId}
            existing={existing}
            existingTwilioConfig={twilioConfig}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface TwilioIntegrationFormProps {
  hotelId: string;
  existing: CommunicationIntegration | null;
  existingTwilioConfig: TwilioIntegrationConfig | null;
  onClose: () => void;
}

const TwilioIntegrationForm: React.FC<TwilioIntegrationFormProps> = ({
  hotelId,
  existing,
  existingTwilioConfig,
  onClose,
}) => {
  const { t } = useTranslation();
  const isFirstSetup = existing === null;
  const isCurrentlyEnabled = existing?.enabled ?? false;
  const canClearSecrets = !!existing && !isCurrentlyEnabled;
  const { mutate: upsert, isPending } = useUpsertTwilioIntegration(hotelId);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TwilioIntegrationFormData>({
    resolver: zodResolver(
      createTwilioIntegrationSchema({ isFirstSetup, isCurrentlyEnabled }),
    ),
    defaultValues: {
      enabled: isCurrentlyEnabled,
      accountSid: existingTwilioConfig?.accountSid ?? "",
      authToken: "",
      clearAuthToken: false,
      smsPhoneNumber: existingTwilioConfig?.smsPhoneNumber ?? "",
      clearSmsPhoneNumber: false,
    },
  });

  const onSubmit = (data: TwilioIntegrationFormData) => {
    upsert(buildTwilioIntegrationPayload(data), {
      onSuccess: () => {
        toast.success(t("communicationChannels.twilio.saveSuccess"));
        onClose();
      },
      onError: () => {
        toast.error(t("communicationChannels.twilio.saveError"));
      },
    });
  };

  const enabled = watch("enabled");
  const authToken = watch("authToken");
  const authTokenCleared = watch("clearAuthToken");
  const smsPhoneNumber = watch("smsPhoneNumber");
  const smsCleared = watch("clearSmsPhoneNumber");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex items-center justify-between rounded-md border p-3">
        <div className="flex flex-col">
          <Label>{t("communicationChannels.twilio.enabledLabel")}</Label>
          <span className="text-xs text-muted-foreground">
            {t("communicationChannels.twilio.enabledHelp")}
          </span>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(value) => setValue("enabled", value)}
          aria-label={t("communicationChannels.twilio.enabledLabel")}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="twilio-account-sid">
          {t("communicationChannels.twilio.fields.accountSid")} *
        </Label>
        <Input
          id="twilio-account-sid"
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          autoComplete="off"
          {...register("accountSid")}
        />
        {errors.accountSid && (
          <p className="text-xs text-destructive">
            {errors.accountSid.message}
          </p>
        )}
      </div>

      <SecretField
        id="twilio-auth-token"
        label={t("communicationChannels.twilio.fields.authToken")}
        value={authToken}
        cleared={authTokenCleared}
        isFirstSetup={isFirstSetup}
        canClear={canClearSecrets}
        error={errors.authToken?.message}
        onChange={(value) =>
          setValue("authToken", value, { shouldValidate: true })
        }
        onToggleClear={(next) =>
          setValue("clearAuthToken", next, { shouldValidate: true })
        }
      />

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="twilio-sms-phone">
          {t("communicationChannels.twilio.fields.smsPhoneNumber")}
        </Label>
        <div className="flex items-start gap-2">
          <Input
            id="twilio-sms-phone"
            placeholder="+15551234567"
            autoComplete="off"
            value={smsPhoneNumber}
            disabled={smsCleared}
            onChange={(event) =>
              setValue("smsPhoneNumber", event.target.value, {
                shouldValidate: true,
              })
            }
            className="flex-1"
          />
          {!isFirstSetup && (
            <Button
              type="button"
              variant="new_outline"
              size="sm"
              disabled={smsPhoneNumber.length > 0 && !smsCleared}
              onClick={() =>
                setValue("clearSmsPhoneNumber", !smsCleared, {
                  shouldValidate: true,
                })
              }
            >
              {smsCleared
                ? t("communicationChannels.secret.clearArmed")
                : t("communicationChannels.secret.clear")}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("communicationChannels.twilio.fields.smsPhoneNumberHelp")}
        </p>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onClose}
          disabled={isPending}
        >
          {t("communicationChannels.actions.cancel")}
        </Button>
        <Button type="submit" variant="new_default" disabled={isPending}>
          {isPending
            ? t("communicationChannels.actions.saving")
            : t("communicationChannels.actions.save")}
        </Button>
      </DialogFooter>
    </form>
  );
};
